import React, { useEffect, useState } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { useHandoffStore } from '../../store/handoffStore';
import { SpeechTextarea } from '../../components/form/SpeechToTextButton';
import type { ModuleKey } from '../../components/layout/AppLayout';

// ── Formular-Definitionen ─────────────────────────────────────────────────

type FormularTyp =
  | 'Eignungszeugnis'
  | 'Tauglichkeitsbescheinigung'
  | 'Befundbrief'
  | 'ASA-Protokoll'
  | 'GBU-Deckblatt';

interface FormularDef {
  typ:         FormularTyp;
  icon:        string;
  beschreibung: string;
  felder:      FormFeld[];
}

interface FormFeld {
  key:         string;
  label:       string;
  typ:         'text' | 'date' | 'time' | 'textarea' | 'select' | 'radio';
  optionen?:   string[];
  placeholder?: string;
  required?:   boolean;
}

const FORMULARE: FormularDef[] = [
  {
    typ: 'Eignungszeugnis', icon: '✅',
    beschreibung: 'Bescheinigung der Eignung für eine bestimmte Tätigkeit gem. §49 ASchG',
    felder: [
      { key: 'arbeitnehmer', label: 'Arbeitnehmer/in', typ: 'text', required: true, placeholder: 'Vor- und Zuname' },
      { key: 'geburtsdatum', label: 'Geburtsdatum', typ: 'date', required: true },
      { key: 'taetigkeit',   label: 'Tätigkeit / Arbeitsplatz', typ: 'text', required: true },
      { key: 'ergebnis',     label: 'Ergebnis', typ: 'radio',
        optionen: ['geeignet', 'bedingt geeignet', 'nicht geeignet'], required: true },
      { key: 'auflagen',     label: 'Auflagen / Einschränkungen', typ: 'textarea', placeholder: 'Falls bedingt geeignet…' },
      { key: 'datum',        label: 'Untersuchungsdatum', typ: 'date', required: true },
      { key: 'uhrzeit_von',  label: 'Uhrzeit von', typ: 'time' },
      { key: 'uhrzeit_bis',  label: 'Uhrzeit bis (optional)', typ: 'time' },
      { key: 'naechste',     label: 'Nächste Untersuchung', typ: 'date' },
    ],
  },
  {
    typ: 'Tauglichkeitsbescheinigung', icon: '📋',
    beschreibung: 'Bescheinigung für spezifische Tätigkeiten (Bildschirm, Fahrerlaubnis etc.)',
    felder: [
      { key: 'arbeitnehmer', label: 'Arbeitnehmer/in', typ: 'text', required: true },
      { key: 'geburtsdatum', label: 'Geburtsdatum', typ: 'date' },
      { key: 'bescheinigung', label: 'Art der Bescheinigung', typ: 'select',
        optionen: ['Bildschirmarbeitsplatztauglichkeit', 'Fahrtauglichkeit', 'Tragezeugnis Atemschutz', 'Absturzsicherung', 'Sonstige'] },
      { key: 'tauglich', label: 'Beurteilung', typ: 'radio',
        optionen: ['tauglich', 'bedingt tauglich', 'nicht tauglich'], required: true },
      { key: 'datum', label: 'Datum', typ: 'date', required: true },
      { key: 'uhrzeit_von', label: 'Uhrzeit von', typ: 'time' },
      { key: 'uhrzeit_bis', label: 'Uhrzeit bis (optional)', typ: 'time' },
      { key: 'gueltigBis', label: 'Gültig bis', typ: 'date' },
    ],
  },
  {
    typ: 'ASA-Protokoll', icon: '🛡',
    beschreibung: 'Protokoll der ASA-Sitzung (Arbeitsschutzausschuss) gem. §11 ASchG',
    felder: [
      { key: 'datum',        label: 'Sitzungsdatum', typ: 'date', required: true },
      { key: 'beginn',       label: 'Beginn (Uhrzeit)', typ: 'time' },
      { key: 'ende',         label: 'Ende (Uhrzeit)', typ: 'time' },
      { key: 'ort',          label: 'Ort', typ: 'text', required: true, placeholder: 'z.B. Konferenzraum EG' },
      { key: 'teilnehmer',   label: 'Teilnehmer', typ: 'textarea', placeholder: 'Name, Funktion – zeilenweise' },
      { key: 'tagesordnung', label: 'Tagesordnung', typ: 'textarea', required: true,
        placeholder: '1. Unfallstatistik\n2. Stand Vorsorgeuntersuchungen\n3. Begehungsergebnisse\n4. Allfälliges' },
      { key: 'beschluesse',  label: 'Beschlüsse / Maßnahmen', typ: 'textarea' },
      { key: 'naechste',     label: 'Nächste Sitzung', typ: 'date' },
    ],
  },
  {
    typ: 'Befundbrief', icon: '📝',
    beschreibung: 'Ärztlicher Befundbericht nach arbeitsmedizinischer Untersuchung (DSGVO-konform)',
    felder: [
      { key: 'datum',       label: 'Untersuchungsdatum', typ: 'date',     required: true },
      { key: 'uhrzeit_von', label: 'Uhrzeit von', typ: 'time' },
      { key: 'uhrzeit_bis', label: 'Uhrzeit bis (optional)', typ: 'time' },
      { key: 'anlass',      label: 'Anlass', typ: 'select',
        optionen: ['VGÜ-Erstuntersuchung', 'VGÜ-Folgeuntersuchung', 'Einstellungsuntersuchung', 'Eignungsuntersuchung', 'Sonstige'], required: true },
      { key: 'tatbestand',  label: 'VGÜ-Tatbestand', typ: 'text',        placeholder: 'z.B. L1 – Lärm' },
      { key: 'ergebnis',    label: 'Befundergebnis', typ: 'radio',
        optionen: ['geeignet', 'bedingt geeignet', 'nicht geeignet'],    required: true },
      { key: 'befund',      label: 'Befundbeschreibung', typ: 'textarea', placeholder: 'Medizinische Befundbeschreibung…' },
      { key: 'massnahmen',  label: 'Empfohlene Maßnahmen', typ: 'textarea' },
      { key: 'naechste',    label: 'Nächste Untersuchung', typ: 'date' },
      { key: 'arzt',        label: 'Unterfertigender Arzt', typ: 'text',  placeholder: 'Dr. med. Klaus Karrer, MSc.' },
    ],
  },
  {
    typ: 'GBU-Deckblatt', icon: '📊',
    beschreibung: 'Deckblatt Gefährdungsbeurteilung gem. §4 ASchG',
    felder: [
      { key: 'bereich',    label: 'Arbeitsbereich / Arbeitsplatz', typ: 'text', required: true },
      { key: 'erstellt',   label: 'Erstellt am', typ: 'date', required: true },
      { key: 'uhrzeit',    label: 'Uhrzeit (optional)', typ: 'time' },
      { key: 'ersteller',  label: 'Erstellt von', typ: 'text', placeholder: 'Dr. Klaus Karrer' },
      { key: 'version',    label: 'Version', typ: 'text', placeholder: '1.0' },
      { key: 'freigabe',   label: 'Freigabe durch', typ: 'text' },
      { key: 'naechste',   label: 'Nächste Überprüfung', typ: 'date' },
      { key: 'anmerkungen', label: 'Anmerkungen', typ: 'textarea' },
    ],
  },
];

// ── Formular-Renderer ─────────────────────────────────────────────────────

function FormularEditor({ def, mandantName, onBack, initial }: {
  def:          FormularDef;
  mandantName:  string;
  onBack:       () => void;
  initial?:     Record<string, string>;
}): React.JSX.Element {
  const [werte, setWerte]     = useState<Record<string, string>>(initial ?? {});
  const [gedruckt, setGedruckt] = useState(false);

  const set = (key: string, val: string): void => {
    setWerte(prev => ({ ...prev, [key]: val }));
  };

  const handlePrint = (): void => {
    setGedruckt(true);
    window.print();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
          ← Zurück
        </button>
        <div>
          <h2 className="font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <span>{def.icon}</span>
            <span>{def.typ}</span>
          </h2>
          <p className="text-xs text-gray-400">{mandantName}</p>
        </div>
      </div>

      <div className="card space-y-4">
        {def.felder.map(f => (
          <div key={f.key} className="space-y-1">
            <label className="label-text">
              {f.label}
              {f.required === true && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {f.typ === 'text' && (
              <input type="text" value={werte[f.key] ?? ''} onChange={e => { set(f.key, e.target.value); }}
                placeholder={f.placeholder} className="input-field" />
            )}
            {f.typ === 'date' && (
              <input type="date" value={werte[f.key] ?? ''} onChange={e => { set(f.key, e.target.value); }}
                className="input-field" />
            )}
            {f.typ === 'time' && (
              <input type="time" value={werte[f.key] ?? ''} onChange={e => { set(f.key, e.target.value); }}
                className="input-field" />
            )}
            {f.typ === 'textarea' && (
              <SpeechTextarea
                value={werte[f.key] ?? ''}
                onChange={v => { set(f.key, v); }}
                rows={4}
                {...(f.placeholder !== undefined ? { placeholder: f.placeholder } : {})}
              />
            )}
            {f.typ === 'select' && f.optionen !== undefined && (
              <select value={werte[f.key] ?? ''} onChange={e => { set(f.key, e.target.value); }}
                className="input-field">
                <option value="">— bitte wählen —</option>
                {f.optionen.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {f.typ === 'radio' && f.optionen !== undefined && (
              <div className="flex flex-wrap gap-3 pt-1">
                {f.optionen.map(o => (
                  <label key={o} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name={f.key} value={o}
                      checked={werte[f.key] === o}
                      onChange={() => { set(f.key, o); }}
                      className="text-blue-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{o}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="flex gap-2 justify-end pt-2">
          {gedruckt && (
            <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
              ✓ Gedruckt
            </span>
          )}
          <button onClick={handlePrint} className="btn-primary">
            🖨 Drucken / PDF
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400 text-center">
        Formulare werden lokal generiert · keine Daten verlassen das Gerät
      </p>
    </div>
  );
}

// ── Vorbefüllung aus einer erfassten Quelle (Besprechungsnotiz/Foto) ──────

/** Hauptsächliches Freitext-Feld je Formulartyp (dorthin wandert die Notiz). */
const FREITEXT_FELD: Partial<Record<FormularTyp, string>> = {
  'Eignungszeugnis':   'auflagen',
  'ASA-Protokoll':     'tagesordnung',
  'Befundbrief':       'befund',
  'GBU-Deckblatt':     'anmerkungen',
};

function buildInitial(def: FormularDef, quelle: { datum?: string; text?: string }): Record<string, string> {
  const initial: Record<string, string> = {};
  // Datum in das erste Datumsfeld
  if (quelle.datum !== undefined && quelle.datum !== '') {
    const dateKey = def.felder.find(f => f.key === 'datum')?.key
      ?? def.felder.find(f => f.typ === 'date')?.key;
    if (dateKey !== undefined) initial[dateKey] = quelle.datum.slice(0, 10);
  }
  // Notiztext in das Haupt-Freitextfeld
  const freitextKey = FREITEXT_FELD[def.typ];
  if (freitextKey !== undefined && quelle.text !== undefined && quelle.text !== '') {
    initial[freitextKey] = quelle.text;
  }
  return initial;
}

// ── Hauptkomponente ───────────────────────────────────────────────────────

export function FormulareModul({ navigate }: { navigate: (m: ModuleKey) => void }): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const consume = useHandoffStore(s => s.consume);
  const [selected, setSelected] = useState<FormularDef | null>(null);
  const [initial,  setInitial]  = useState<Record<string, string> | undefined>(undefined);

  // Übergabe aus der Erfassung: Formular vorbefüllt öffnen
  useEffect(() => {
    const h = consume();
    if (h !== null && h.action === 'formular') {
      const def = FORMULARE.find(f => f.typ === h.formularTyp);
      if (def !== undefined) {
        setInitial(buildInitial(def, h.quelle));
        setSelected(def);
      }
    }
  }, [consume]);

  if (selected !== null) {
    return (
      <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto w-full">
        <FormularEditor
          def={selected}
          mandantName={mandant?.name ?? 'Alle Mandanten'}
          onBack={() => { setSelected(null); setInitial(undefined); }}
          {...(initial !== undefined ? { initial } : {})}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Formulare</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Arbeitsmedizinische Standardformulare — ausfüllen & drucken
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FORMULARE.map(f => (
          <button key={f.typ}
            onClick={() => { setSelected(f); }}
            className="card text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer group">
            <div className="text-3xl mb-3">{f.icon}</div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
              {f.typ}
            </h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{f.beschreibung}</p>
            <p className="text-xs text-blue-500 mt-3 font-medium">
              {f.felder.length} Felder → ausfüllen &amp; drucken
            </p>
          </button>
        ))}

        {/* Besprechungsprotokoll — bearbeitbar & speicherbar (eigenes Modul) */}
        <button
          onClick={() => { navigate('protokolle'); }}
          className="card text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer group border-blue-100 dark:border-blue-950/40">
          <div className="text-3xl mb-3">📝</div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
            Besprechungsprotokoll
          </h3>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
            Notizen &amp; Aufgaben zu einem Termin — bearbeitbar, gespeichert, mit Kalender verknüpfbar.
          </p>
          <p className="text-xs text-blue-500 mt-3 font-medium">
            bearbeiten &amp; speichern →
          </p>
        </button>
      </div>

      <p className="text-xs text-gray-400 text-center">
        {FORMULARE.length} Druck-Formulare + Besprechungsprotokoll · alle DSGVO-konform · lokal im Browser
      </p>
    </div>
  );
}
